import React, { useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import { XDataGrid } from '@core/components-js-only';
import { XRaccoon } from '@core/utils';
import { TowaryReadonly, TowarId } from '@core/utils/types';

// devExtreme
import {
  Column,
  FilterRow,
  Grouping,
  GroupPanel
} from 'devextreme-react/data-grid';

// components
import CountColumnItem from './CountColumnItem/CountColumnItem';
import CellNazwaRender from './CellNazwaRender/CellNazwaRender';

// types
import {
  KategoriaTowaryProps,
  DataGridRef,
  TowarChangedData,
  CountColumnItemsInputs
} from '../types';

// i18n
import { useTranslation } from '@core/hooks';

const KategoriaTowaryGrid: React.FC<KategoriaTowaryProps> = ({
  kategoriaId,
  searchValue,
  dataGridRef,
  focusedRowEnabled = false,
  commonMainInfoColumn = false,
  dxDataGridCustomOpts,
  title,
  height,
  toolbarOptions,
  onCartUpdated,
  addToCartEnabled = false,
  onlyCartAdded = false,
  showPrice = true,
  children
}) => {
  const towarChangedData = useRef<TowarChangedData>({});
  const countColumnItemsInputs = useRef<CountColumnItemsInputs>({});
  const firstMount = useRef(true);
  const defaultDataGridRef = useRef<DataGridRef>(null);
  const determinedDataGridRef = dataGridRef || defaultDataGridRef;

  const shouldBlockGridFocus = useRef(false);

  const onDataLoaded = (towary: TowaryReadonly) => {
    const loadedTowaryIds = towary.map((x) => x.Id);

    loadedTowaryIds.forEach((id) => {
      if (towarChangedData.current[id]) {
        towarChangedData.current[id] = null;
      }
    });
  };

  const dataSource = useMemo(
    () =>
      XRaccoon.Api.towaryXSearchDataSource(
        {
          KategoriaId: kategoriaId,
          PobierzIloscWKoszyku: onlyCartAdded
        },
        onDataLoaded
      ),
    [kategoriaId, onlyCartAdded]
  );

  useLayoutEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }

    if (!determinedDataGridRef.current) return;

    shouldBlockGridFocus.current = true;
    dataSource.searchValue(searchValue);
    determinedDataGridRef.current.instance.refresh();
  }, [searchValue]);

  const cellCartRender = useCallback(({ data }) => {
    if (!addToCartEnabled || !data) return null;

    const { data: rowData, rowIndex } = data;

    if (!rowData) return null;

    const {
      Id,
      IloscTowarowWKoszyku
    }: {
      Id: TowarId;
      IloscTowarowWKoszyku: number;
    } = rowData;

    const ilosc = towarChangedData.current[Id] ?? IloscTowarowWKoszyku;

    return (
      <CountColumnItem
        dataGridRef={determinedDataGridRef}
        towarChangedData={towarChangedData}
        countColumnItemsInputs={countColumnItemsInputs}
        rowIndex={rowIndex}
        onCartUpdated={onCartUpdated}
        towarId={Id}
        initValue={ilosc}
      />
    );
  }, []);

  const cellWartoscZamowieniaCalcDispVal = useCallback(
    ({
      Id,
      IloscTowarowWKoszyku,
      Wartosc
    }: {
      Id: TowarId;
      IloscTowarowWKoszyku: number;
      Wartosc: number;
    }) => {
      const ilosc = towarChangedData.current[Id] ?? IloscTowarowWKoszyku;
      const wartoscZamDisplayVal = Wartosc * ilosc;

      return wartoscZamDisplayVal;
    },
    []
  );

  const { t } = useTranslation();

  return (
    <XDataGrid
      refs={determinedDataGridRef}
      dataSource={dataSource}
      focusedRowEnabled={focusedRowEnabled}
      sortingMode="single"
      // remoteOperations
      shouldBlockGridFocus={shouldBlockGridFocus}
      title={title}
      disableToolbar={!toolbarOptions}
      toolbarOptions={toolbarOptions}
      height={height}
      showSearchPanel={false}
      {...dxDataGridCustomOpts}
    >
      {children}
      <Grouping contextMenuEnabled={false} />
      <GroupPanel visible={false} />
      <FilterRow visible={false} />
      <Column
        dataField="Indeks"
        caption={t('Indeks')}
        visible={!commonMainInfoColumn}
        allowSorting={false}
      />
      <Column
        dataField="Ean"
        caption={t('Ean')}
        visible={!commonMainInfoColumn}
        allowSorting={false}
      />
      <Column
        dataField="Nazwa"
        caption={t('Nazwa')}
        width={commonMainInfoColumn ? '55%' : '45%'}
        cellRender={commonMainInfoColumn ? CellNazwaRender : undefined}
      />
      <Column dataField="ModelNazwa" caption={t('Model')} />
      <Column
        dataField="Wartosc"
        dataType="number"
        cssClass="kategoria-towary-grid-wartosc-cell"
        format="#0.00"
        caption={t('Cena')}
        visible={showPrice}
      />
      <Column
        dataField="WartoscZamowienia"
        visible={addToCartEnabled}
        cssClass="kategoria-towary-grid-wartosc-cell"
        calculateDisplayValue={cellWartoscZamowieniaCalcDispVal}
        dataType="number"
        format="#0.00"
        allowSorting={false}
        allowFiltering={false}
        caption={t('Wartosc')}
      />
      <Column
        dataField="AddToCartAction"
        visible={addToCartEnabled}
        fixed
        fixedPosition="right"
        width="auto"
        caption={t('Ilosc')}
        alignment="center"
        cellComponent={cellCartRender}
        allowSearch={false}
        allowResizing={false}
        allowGrouping={false}
        allowFiltering={false}
        allowSorting={false}
      />
    </XDataGrid>
  );
};

export default KategoriaTowaryGrid;
